/**
 * Zustand store del módulo de Análisis de Vigas.
 *
 * El store mantiene UN solo `BeamAnalysisRequest` activo. A diferencia del
 * módulo de Diseño que indexa por tipo de viga (rectangular, peraltada,
 * etc.), aquí no es necesario: el análisis depende de geometría + apoyo
 * + cargas, no del "tipo de sección".
 */

import { create } from 'zustand';
import type {
  BeamAnalysisAutoLoads,
  BeamAnalysisGeometry,
  BeamAnalysisRequest,
  CombinationKind,
  DistributedLoadExtra,
  PointLoad,
  SeismicInput,
  SupportType,
  WallOnBeam,
} from './types';

interface BeamAnalysisState {
  request: BeamAnalysisRequest;
  setGeometry: (patch: Partial<BeamAnalysisGeometry>) => void;
  setSupportType: (t: SupportType) => void;
  setAutoLoads: (patch: Partial<BeamAnalysisAutoLoads>) => void;
  setWallOnBeam: (w: WallOnBeam | null) => void;
  setCombo: (c: CombinationKind) => void;
  setMaterials: (patch: Partial<Pick<BeamAnalysisRequest, 'fc' | 'fy' | 'cover_cm' | 'TMA_cm'>>) => void;

  // Cargas distribuidas adicionales
  addDistributed: () => void;
  updateDistributed: (idx: number, patch: Partial<DistributedLoadExtra>) => void;
  removeDistributed: (idx: number) => void;

  // Cargas puntuales
  addPointLoad: () => void;
  updatePointLoad: (idx: number, patch: Partial<PointLoad>) => void;
  removePointLoad: (idx: number) => void;

  // Sismo
  toggleSeismic: (on: boolean) => void;
  setSeismic: (patch: Partial<SeismicInput>) => void;

  // Hidratación (al cargar un análisis guardado)
  loadRequest: (req: BeamAnalysisRequest) => void;
}

const defaultRequest: BeamAnalysisRequest = {
  geometry: { b: 30, h: 60, L_span_m: 5.0 },
  support_type: 'continuous_interior',
  auto_loads: {
    use: 'vivienda',
    slab_type: 'aligerada_20',
    tributary_width_m: 3.5,
    acabados_kgf_m2: 100,
    tabiqueria_kgf_m2: 100,
    cielo_raso_kgf_m2: 30,
    wall_on_beam: null,
    custom_live_load_kgf_m2: null,
  },
  extra_distributed: [],
  point_loads: [],
  seismic: null,
  combo: 'E060',
  fc: 210,
  fy: 4200,
  cover_cm: 4,
  TMA_cm: 1.905,
};

const defaultSeismic: SeismicInput = {
  zone: 'Z4',
  usage: 'C',
  soil: 'S2',
  system: 'porticos_concreto',
  building_height_m: 12.0,
  seismic_weight_kgf: 300_000,
  floor_shear_fraction: 1.0,
  beam_shear_fraction: 0.25,
};

export const useBeamAnalysisStore = create<BeamAnalysisState>((set) => ({
  request: defaultRequest,

  setGeometry: (patch) =>
    set((s) => ({
      request: { ...s.request, geometry: { ...s.request.geometry, ...patch } },
    })),
  setSupportType: (t) => set((s) => ({ request: { ...s.request, support_type: t } })),
  setAutoLoads: (patch) =>
    set((s) => ({
      request: { ...s.request, auto_loads: { ...s.request.auto_loads, ...patch } },
    })),
  setWallOnBeam: (w) =>
    set((s) => ({
      request: { ...s.request, auto_loads: { ...s.request.auto_loads, wall_on_beam: w } },
    })),
  setCombo: (c) => set((s) => ({ request: { ...s.request, combo: c } })),
  setMaterials: (patch) =>
    set((s) => ({ request: { ...s.request, ...patch } })),

  addDistributed: () =>
    set((s) => ({
      request: {
        ...s.request,
        extra_distributed: [
          ...s.request.extra_distributed,
          { kind: 'dead', magnitude: 100, x_start_cm: 0, x_end_cm: 0, label: '' },
        ],
      },
    })),
  updateDistributed: (idx, patch) =>
    set((s) => ({
      request: {
        ...s.request,
        extra_distributed: s.request.extra_distributed.map((d, i) =>
          i === idx ? { ...d, ...patch } : d,
        ),
      },
    })),
  removeDistributed: (idx) =>
    set((s) => ({
      request: {
        ...s.request,
        extra_distributed: s.request.extra_distributed.filter((_, i) => i !== idx),
      },
    })),

  addPointLoad: () =>
    set((s) => ({
      request: {
        ...s.request,
        point_loads: [
          ...s.request.point_loads,
          {
            kind: 'dead',
            magnitude_kgf: 1000,
            x_cm: s.request.geometry.L_span_m * 50, // mitad por defecto
            label: '',
          },
        ],
      },
    })),
  updatePointLoad: (idx, patch) =>
    set((s) => ({
      request: {
        ...s.request,
        point_loads: s.request.point_loads.map((p, i) =>
          i === idx ? { ...p, ...patch } : p,
        ),
      },
    })),
  removePointLoad: (idx) =>
    set((s) => ({
      request: {
        ...s.request,
        point_loads: s.request.point_loads.filter((_, i) => i !== idx),
      },
    })),

  toggleSeismic: (on) =>
    set((s) => ({
      request: { ...s.request, seismic: on ? (s.request.seismic ?? { ...defaultSeismic }) : null },
    })),
  setSeismic: (patch) =>
    set((s) => ({
      request: {
        ...s.request,
        seismic: s.request.seismic ? { ...s.request.seismic, ...patch } : { ...defaultSeismic, ...patch },
      },
    })),

  loadRequest: (req) => set({ request: req }),
}));
